import withRouter from "Components/Common/withRouter"
import IndustryEngagementForm from "Components/organisms/forms/industry-engagement-form"
import IndustryEngagementFields from "Components/organisms/forms/industry-engagement-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import useManual from "hooks/useManual"
import { IndustryEngagementValidation } from "data/industry-engagement"
import { FormikHelpers, useFormik } from "formik"
import Employee from "models/employee"
import { IndustryEngagementStatus } from "models/industry-engagement"
import Opportunity from "models/opportunity"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import {
    addIndustryEngagement,
    IndustryEngagementSliceState,
    setIndustryEngagementSearchFields
} from "slices/business-development/industry-engagement/reducer"
import {
    getIndustryEngagementSearchFields,
    submitCreateIndustryEngagementForm
} from "slices/thunk"
import Modal from "Components/molecule/modal"
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"


const selectIndustryEngagement = createSelector(
    (state: any) => state.IndustryEngagement,
    ({
        statuses,
        employees,
        opportunities,
    }: IndustryEngagementSliceState) => ({
        statuses,
        employees,
        opportunities,
    })
)

const CreateIndustryEngagementPage = (props: any) => {
    document.title = "Create Industry Engagement | IbexIQ";
    const { handleOpenManual } = useManual("business-development/industry-engagement");
    const dispatch: any = useDispatch();
    const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)

    const { statuses, employees, opportunities } = useSelector(selectIndustryEngagement)

    const initializePage = async () => {
        if (statuses.length === 0 || employees.length === 0 || opportunities.length === 0) {
            const res = await getIndustryEngagementSearchFields()
            if (!res) return;
            dispatch(setIndustryEngagementSearchFields(res))
        }
    }

    useEffect(() => {
        initializePage()
    }, [])

    const handleSubmitForm = (_vals: IndustryEngagementFields, helpers: FormikHelpers<IndustryEngagementFields>) => {
        helpers.setSubmitting(false)
        setShowAreYouSureModal(true)
    }

    const initialValues: IndustryEngagementFields = useMemo(() => ({
        name: '',
        date: '',
        description: '',
        statusId: '',
        attendeeIds: [],
        opportunityIds: []
    }), [])

    const {
        values,
        errors,
        touched,
        handleSubmit,
        handleBlur,
        handleChange,
        isValid,
        setSubmitting,
        isSubmitting,
        handleReset,
        setFieldValue
    } = useFormik({
        onSubmit: handleSubmitForm,
        enableReinitialize: true,
        initialValues,
        validationSchema: IndustryEngagementValidation
    })

    const handleBack = () => {
        props.router.navigate(-1)
        setShowAreYouSureModal(false)
    }

    const handleModalClose = () => setShowAreYouSureModal(false)

    const handleConfirmSubmit = useCallback(async (
        values: IndustryEngagementFields,
        setSubmitting: (val: boolean) => any,
        reset: (e: any) => void
    ) => {
        setSubmitting(true);
        const res = await submitCreateIndustryEngagementForm(values, () => reset({}))

        if (!res) {
            handleModalClose()
        } else {
            handleBack()
            dispatch(addIndustryEngagement(res))
        }
        setSubmitting(false)
    }, [handleBack, handleModalClose])

    const statusOptions = useMemo(() => statuses.map((s: IndustryEngagementStatus) => ({
        value: s.id,
        label: s.description
    })), [statuses])

    const employeeOptions = useMemo(() => employees.map((e: Employee) => ({
        value: e.id,
        label: e.commonName ?? `${e.firstName} ${e.lastName}`
    })), [employees])

    const opportunityOptions = useMemo(() => opportunities.map((o: Opportunity) => ({
        value: o.id,
        label: o.description
    })), [opportunities])

    return <FormPageTemplate
        title={"Business Development"}
        breadcrumbItem={"Create Industry Engagement"}
        actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
        form={<IndustryEngagementForm
            statuses={statusOptions}
            employees={employeeOptions}
            opportunities={opportunityOptions}
            values={values}
            errors={errors}
            touched={touched}
            handleChange={handleChange}
            handleBlur={handleBlur}
            handleSubmit={handleSubmit}
            handleCancel={handleBack}
            setFieldValue={setFieldValue}
            isValid={isValid}
            isSubmitting={isSubmitting}
        />}
        isLoading={false}
        modal={<Modal
            isOpen={showAreYouSureModal}
            onClose={handleModalClose}
            title={"Confirm"}
            subtitle={"Are you sure you want to create this industry engagement?"}
        >
            <AreYouSureButtons
                disable={isSubmitting}
                acceptButtonStyle={{ width: '150px' }}
                declineButtonStyle={{ width: '150px' }}
                handleAccept={() => handleConfirmSubmit(values, setSubmitting, handleReset)}
                handleDecline={handleModalClose}
                acceptText={isSubmitting ? "Submitting..." : "Submit"}
                declineText={"Cancel"}
            />
        </Modal>}
    />
}

export default withRouter(CreateIndustryEngagementPage)